import { createZonedMoment } from '@src/utils/userTimeZone';
import api from '@src/api';
import * as apiResponseCache from '@src/db/apiResponseCache';
import { TimeClockPunchResult, RestPunchRecord } from '@src/services/punchIn';
import { IGetPunchInRecordsResponse } from '@src/services/attendance';

export interface MachinePunchContext {
	punch_in_type?: string;
	rest_punch_required?: boolean;
}

export interface MachineShift {
	id: string;
	name: string;
	status: boolean;
	retired_at?: string | null;
}

const SHIFTS_CACHE_KEY = 'machine-punch/shifts';

export async function getCachedMachineContext(accountId: string): Promise<MachinePunchContext> {
	const cached = await apiResponseCache.getResponse(accountId, 'punch-in-info/');
	return (cached?.data || {}) as MachinePunchContext;
}

export async function getMachineShiftOptions(accountId: string): Promise<MachineShift[]> {
	try {
		const shifts: MachineShift[] = [];
		let page = 0;
		let totalCount = Number.POSITIVE_INFINITY;
		while (shifts.length < totalCount) {
			// Pages must be fetched in order; the response supplies the total count.
			// eslint-disable-next-line no-await-in-loop
			const response: { data: MachineShift[]; pagination: { totalCount: number } } = await api.get(
				'shifts/', { page, page_size: 100 },
				{ hasPagination: true, suppressErrorToast: true, cacheAccountId: accountId },
			);
			shifts.push(...response.data);
			totalCount = response.pagination.totalCount;
			if (response.data.length < 100 || !Number.isFinite(totalCount)) break;
			page += 1;
		}
		const available = shifts.filter(shift => shift.status && !shift.retired_at);
		await apiResponseCache.putResponse({
			accountId, url: SHIFTS_CACHE_KEY, status: 200, headers: {}, data: available,
		});
		return available;
	} catch (error) {
		const status = (error as { response?: { status?: number } })?.response?.status;
		if (status != null && status < 500) throw error;
		const cached = await apiResponseCache.getResponse(accountId, SHIFTS_CACHE_KEY);
		if (!cached) throw error;
		return cached.data as MachineShift[];
	}
}

export async function cacheMachinePunchContext(accountId: string, data: MachinePunchContext): Promise<void> {
	await Promise.allSettled([
		apiResponseCache.putResponse({ accountId, url: 'punch-in-info/', status: 200, headers: {}, data }),
		getMachineShiftOptions(accountId),
	]);
}

// The existing worker publishes the formal record ID. Resolve its authoritative details
// before handing off the local row, including work -> rest corrections.
export async function getMachinePunchResult(
	recordId: string,
	accountId: string,
	punchedAt: number,
	timezone?: string,
): Promise<TimeClockPunchResult> {
	const date = createZonedMoment(timezone)(punchedAt).format('YYYY-MM-DD');
	const response: IGetPunchInRecordsResponse = await api.get(
		'punch-in-records/', { user_id: accountId, query_date: date }, { suppressErrorToast: true },
	);
	const work = response.records.find(record => record.id === recordId);
	if (work) {
		return {
			id: work.id,
			shift_name: work.shift_name,
			duty: work.duty,
			punch_at: work.punch_datetime,
			record_type: 'work',
			record_created_at: work.record_created_at,
		};
	}
	const restRecords: RestPunchRecord[] = await api.get(
		'punch-in-records/rest-records', { query_date: date }, { suppressErrorToast: true },
	);
	const rest = restRecords.find(record => record.id === recordId);
	if (rest) {
		return {
			id: rest.id,
			shift_name: rest.shift_name,
			duty: rest.on_break,
			punch_at: rest.punch_at,
			record_type: 'rest',
			record_created_at: rest.record_created_at,
		};
	}
	throw new Error('Machine punch record is not available yet');
}
